import { type MediaDataRoomParticipant } from "../base";
import {
  type RuleBasedAudienceCombine as BaseRuleBasedAudienceCombine,
  type RuleBasedAudienceFilters as BaseRuleBasedAudienceFilters,
} from "./ruleBasedAudience";

export * from "./ruleBasedAudience";

export type AudienceKind = "lookalike" | "advertiser" | "rulebased";

export type AudienceStatus = "published" | "ready";

export interface AdvertiserAudience {
  id: string;
  type: string;
  name: string;
  kind: "advertiser";
  status: AudienceStatus;
  createdAt?: string;
}

export interface LookalikeAudience {
  id: string;
  excludeSeedAudience: boolean;
  kind: "lookalike";
  name: string;
  reach: number;
  status: AudienceStatus;
  createdAt?: string;
  sourceRef: string;
}

type NonNullableObjectFields<V, F extends keyof V> = {
  [K in keyof V]: K extends F ? NonNullable<V[K]> : V[K];
};

export type RuleBasedAudienceFilters = Omit<
  BaseRuleBasedAudienceFilters,
  "filters"
> & {
  filters: NonNullableObjectFields<
    BaseRuleBasedAudienceFilters["filters"][number],
    "attribute" | "condition"
  >[];
};

export type RuleBasedAudienceCombine = Omit<
  BaseRuleBasedAudienceCombine,
  "filters" | "name"
> & {
  filters: RuleBasedAudienceFilters | null;
};

export interface RuleBasedAudience {
  id: string;
  kind: "rulebased";
  name: string;
  status: AudienceStatus;
  createdAt?: string;
  sourceRef: string;
  filters: RuleBasedAudienceFilters | null;
  combine: RuleBasedAudienceCombine[] | null;
}

export type Audience =
  | AdvertiserAudience
  | LookalikeAudience
  | RuleBasedAudience;

export interface AudienceV3ErrorReport {
  missingCustomAudiences: string[];
  missingFilterAttributes: string[];
  missingFilterValues: string[];
  missingSeedAudiences: string[];
  fullMessage: string;
}

export type AudienceV3Status = AudienceStatus | "error";

interface AudienceV3Extension {
  doesDependOnLookalikeAudience: boolean;
  canBeViewedByCurrentUser: boolean;
  sharedWith: MediaDataRoomParticipant[];
  createdBy: string;
  errorReport: AudienceV3ErrorReport | null;
  isStale: boolean;
  sourceRefKind: "base" | "custom" | "seed";
}

export type LookalikeAudienceV3 = LookalikeAudience & AudienceV3Extension;
export type RuleBasedAudienceV3 = RuleBasedAudience & AudienceV3Extension;
export type AdvertiserAudienceV3 = AdvertiserAudience &
  AudienceV3Extension & { sourceRef: string };

export type AudienceV3 =
  | AdvertiserAudienceV3
  | LookalikeAudienceV3
  | RuleBasedAudienceV3;

interface AudienceAbStatusExtension {
  isPublishedAsIntermediate: boolean;
}

export type AdvertiserAudienceAb = AdvertiserAudience &
  AudienceAbStatusExtension & { size?: number };
export type LookalikeAudienceAb = LookalikeAudience & AudienceAbStatusExtension;
export type RuleBasedAudienceAb = RuleBasedAudience & AudienceAbStatusExtension;
export interface SeedAudienceV3 {
  id: string;
  name: string;
  kind: "seed";
  isEmpty: boolean;
}

export type AudienceAb =
  | AdvertiserAudienceAb
  | LookalikeAudienceAb
  | RuleBasedAudienceAb;

export const audienceTypePresentationMap = new Map<AudienceKind, string>([
  ["lookalike", "AI Lookalike"],
  ["advertiser", "Remarketing"],
  ["rulebased", "Rule-based"],
]);

export interface AllUsersAudience {
  id: string;
  name: string;
  kind: "advertiser";
}

export const ALL_PUBLISHER_USERS_AUDIENCE: AllUsersAudience = {
  id: "ALL_PUBLISHER_USERS",
  kind: "advertiser",
  name: "All publisher users",
};

export const ALL_BASE_USERS_AUDIENCE: AllUsersAudience = {
  id: "ALL_BASE_USERS",
  kind: "advertiser",
  name: "All users in the base audience",
};
